// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a card component that displays a button to view an organization's
 * user list in a drawer. The user list itself is loaded asynchronously when the drawer
 * is opened.
 */
import { Organization } from '@/account/api/types';
import { OrganizationUserList } from '@/account/components/organization-user-list';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  CloseRounded,
  NavigateNextRounded,
} from '@/constants/hardfin-standard-icons';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Suspense } from 'react';

interface Props {
  /**
   * The organization whose users should be displayed
   */
  organization: Organization;
  /**
   * Whether the users drawer is currently open
   */
  isOpen: boolean;
  /**
   * Callback when the drawer should be opened
   */
  onOpen: () => void;
  /**
   * Callback when the drawer should be closed
   */
  onClose: () => void;
}

/**
 * A card component that provides access to an organization's user list through
 * a drawer interface. The user list is loaded asynchronously when the drawer
 * is opened.
 *
 * The component shows a simple card with a "View all users" button. When clicked,
 * it opens a drawer from the right side of the screen containing the full user list.
 */
export function OrganizationUsersCard(props: Props) {
  const theme = useTheme();

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Drawer
        anchor="right"
        open={props.isOpen}
        onClose={props.onClose}
        PaperProps={{ sx: { maxWidth: '60rem', minWidth: '40rem' } }}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <Stack
          spacing={1}
          padding={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1">User list</Typography>
          <IconButton size="small" onClick={props.onClose}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Box padding={2}>
          <SupportErrorBoundary message="An error occurred while loading the user list.">
            <Suspense>
              <OrganizationUserList organizationKey={props.organization.key} />
            </Suspense>
          </SupportErrorBoundary>
        </Box>
      </Drawer>
      <Stack
        useFlexGap
        spacing={1}
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
      >
        <Typography variant="overline" marginRight="auto">
          Users
        </Typography>
        <Button
          type="submit"
          size="small"
          onClick={props.onOpen}
          endIcon={<NavigateNextRounded fontSize="small" />}
        >
          View all users
        </Button>
      </Stack>
    </Paper>
  );
}
